/**
 * 首頁帳號狀態列(學術風)—— 掛在學習堡壘首頁頂端,讓「登入」一眼看得到。
 *
 * boss 2026-06-08:「首頁就應該顯示」—— 登入不該藏在設定裡。
 *   - 未登入:「☁️ 登入帳號 · 跨裝置同步進度  登入 →」,點開彈出登入框。
 *   - 已登入:「☁️ {email} · 已同步」,點開可登出/換帳號。
 *   - `isCloudEnabled()` 為 false(沒設雲端)→ 不渲染(首頁照舊)。
 *
 * 彈窗內直接重用 `renderAccountSection`(同一套 email magic-link 登入 UI),
 * 登入狀態變化(ef:account-changed)→ 更新狀態列、若已登入自動關彈窗。
 */
import { isCloudEnabled, getCurrentUser } from '../shared/supabase';
import { ACCOUNT_CHANGED_EVENT } from '../shared/account';
import { renderAccountSection } from './account-section';

const STRIP_STYLE_ID = 'ef-account-strip-style';

function ensureStripStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STRIP_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STRIP_STYLE_ID;
  style.textContent = `
.account-strip {
  display: flex; align-items: center; gap: 8px; width: 100%;
  box-sizing: border-box; padding: 10px 14px; margin: 0;
  background: var(--ef-paper-bright); border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius); box-shadow: var(--ef-shadow-card);
  font-family: var(--ef-sans); font-size: 14px; color: var(--ef-ink-soft);
  cursor: pointer; text-align: left;
  -webkit-tap-highlight-color: transparent; touch-action: manipulation;
  transition: border-color .16s ease, box-shadow .16s ease;
}
.account-strip:hover { border-color: var(--ef-green); }
.account-strip .as-icon { font-size: 18px; line-height: 1; flex: 0 0 auto; }
.account-strip .as-text { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.account-strip .as-cta { flex: 0 0 auto; color: var(--ef-green-dark); font-weight: 700; }
.account-strip.is-in .as-text { color: var(--ef-ink); font-weight: 600; }

.account-modal-backdrop {
  position: fixed; inset: 0; z-index: 200; display: grid; place-items: center;
  padding: 18px; background: rgba(26, 26, 26, 0.45);
}
.account-modal {
  width: 100%; max-width: 460px; max-height: 86vh; overflow: auto;
  background: var(--ef-paper); border-radius: var(--ef-radius);
  box-shadow: var(--ef-shadow-lift); padding: 12px;
}
.account-modal__close {
  display: block; margin-left: auto; background: transparent; border: none;
  font-size: 22px; line-height: 1; color: var(--ef-sub); cursor: pointer; padding: 4px 8px;
}
/* 彈窗在設定畫面 scope 之外 → 重新套學術風,蓋掉舊的全域深藍/金色 settings 樣式。 */
.account-modal .settings-section {
  background: transparent; border: none; box-shadow: none; padding: 0; margin: 0;
  color: var(--ef-ink-soft);
}
.account-modal .settings-section h3 {
  color: var(--ef-ink); font-family: var(--ef-serif); font-weight: 700;
  font-size: 18px; letter-spacing: .5px; margin: 0 0 10px;
}
.account-modal .primary-btn {
  background: var(--ef-green); color: #fff; border: 1px solid var(--ef-green);
  border-radius: var(--ef-radius-pill); padding: 10px 22px; font-size: 15px; font-weight: 700;
  font-family: var(--ef-sans); cursor: pointer; min-height: 44px;
}
.account-modal .primary-btn:disabled { opacity: .45; cursor: default; }
.account-modal .secondary-btn {
  background: transparent; color: var(--ef-green-dark); border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-pill); padding: 10px 22px; font-size: 15px; font-weight: 700;
  font-family: var(--ef-sans); cursor: pointer; min-height: 44px;
}
.account-modal .secondary-btn:disabled { opacity: .45; cursor: default; }
`;
  document.head.appendChild(style);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c),
  );
}

/** 彈出登入框(重用設定頁的 renderAccountSection)。封面 / 狀態列共用。 */
export function openAccountModal(): void {
  const backdrop = document.createElement('div');
  backdrop.className = 'account-modal-backdrop';
  backdrop.setAttribute('data-testid', 'account-modal');

  const modal = document.createElement('div');
  modal.className = 'account-modal';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'account-modal__close';
  close.setAttribute('aria-label', '關閉');
  close.textContent = '✕';
  modal.appendChild(close);

  const mount = document.createElement('div');
  modal.appendChild(mount);
  const cleanupSection = renderAccountSection(mount);

  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);

  function dismiss(): void {
    cleanupSection();
    window.removeEventListener(ACCOUNT_CHANGED_EVENT, onIn);
    backdrop.remove();
  }
  // 登入成功(account-changed)→ 自動關彈窗。
  const onIn = (): void => { void getCurrentUser().then((u) => { if (u) dismiss(); }); };
  window.addEventListener(ACCOUNT_CHANGED_EVENT, onIn);

  close.addEventListener('click', dismiss);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) dismiss(); });
}

/**
 * 在 `parent` 頂端掛一條帳號狀態列。回傳清理函式;雲端未啟用時 no-op 不渲染。
 */
export function renderAccountStrip(parent: HTMLElement): () => void {
  if (!isCloudEnabled()) return () => {};
  ensureStripStyle();

  const strip = document.createElement('button');
  strip.type = 'button';
  strip.className = 'account-strip';
  strip.setAttribute('data-testid', 'account-strip');
  parent.appendChild(strip);

  async function paint(): Promise<void> {
    let email: string | null = null;
    try { email = (await getCurrentUser())?.email ?? null; } catch { email = null; }
    if (email) {
      strip.classList.add('is-in');
      strip.innerHTML =
        `<span class="as-icon" aria-hidden="true">☁️</span>` +
        `<span class="as-text" data-testid="account-strip-text">${escapeHtml(email)} · 已同步</span>`;
    } else {
      strip.classList.remove('is-in');
      strip.innerHTML =
        `<span class="as-icon" aria-hidden="true">☁️</span>` +
        `<span class="as-text" data-testid="account-strip-text">登入帳號 · 跨裝置同步進度</span>` +
        `<span class="as-cta">登入 →</span>`;
    }
  }
  void paint();

  strip.addEventListener('click', openAccountModal);
  const onChange = (): void => { void paint(); };
  window.addEventListener(ACCOUNT_CHANGED_EVENT, onChange);

  return () => {
    window.removeEventListener(ACCOUNT_CHANGED_EVENT, onChange);
    strip.remove();
  };
}
